import React from "react";
import AttachmentsActions from "components/shared/AttachmentsActions";
import { DatePicker } from "components/shared/form/Datepicker";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

export default function Step3Certifications({
  certificationsForm,
  setCertificationsForm,
  onBack,
  onSubmit,
  isSubmitting,
  companyId,
}) {
  const handleToggleCard = (cardKey, value) => {
    if (isSubmitting) return;
    setCertificationsForm((prev) => {
      const updated = { ...prev };
      if (cardKey === "hasWhiteCard") {
        updated.hasWhiteCard = value;
        if (!value) updated.whiteCard = { ...prev.whiteCard, cardNo: "", expiryDate: "", file: null, files: [] };
      } else if (cardKey === "hasSilicaCard") {
        updated.hasSilicaCard = value;
        if (!value) updated.silica = { ...prev.silica, cardNo: "", expiryDate: "", file: null, files: [] };
      } else if (cardKey === "hasAsbestosCard") {
        updated.hasAsbestosCard = value;
        if (!value) updated.asbestos = { ...prev.asbestos, cardNo: "", expiryDate: "", file: null, files: [] };
      }
      return updated;
    });
  };

  const handleCardFieldChange = (cardKey, field, value) => {
    if (isSubmitting) return;
    setCertificationsForm((prev) => ({
      ...prev,
      [cardKey]: {
        ...(prev[cardKey] || {}),
        [field]: value,
      },
    }));
  };

  // White Card is important for verification but not mandatory to fill
  const isWhiteCardValid = true;

  const renderCardFields = (cardKey, labelTitle, isCardNoRequired = false) => {
    const cardData = certificationsForm[cardKey] || {};
    const rawFileList =
      cardData.files ||
      (cardData.file ? [cardData.file] : null) ||
      cardData.documents ||
      cardData.Documents ||
      [];

    const fileList = (Array.isArray(rawFileList) ? rawFileList : [rawFileList])
      .map((f) => {
        if (!f) return null;
        if (f instanceof File || f instanceof Blob) return f;

        const fileName =
          f.name ||
          f.originalFilename ||
          f.OriginalFilename ||
          f.fileName ||
          f.FileName ||
          "document";

        const fileSize = Number(f.size ?? f.fileSize ?? f.FileSize ?? 0);
        const filePath =
          f.filePath ||
          f.filePathVal ||
          f.fileURL ||
          f.FileURL ||
          f.storedFilename ||
          f.StoredFilename;

        const activeCompanyId = companyId || f.companyId || f.CompanyId || null;
        const activeFeature = f.feature || f.Feature || "UserCertifications";

        return {
          ...f,
          name: fileName,
          size: fileSize,
          filePath: filePath,
          fileURL: filePath,
          companyId: activeCompanyId,
          CompanyId: activeCompanyId,
          feature: activeFeature,
          Feature: activeFeature,
        };
      })
      .filter(Boolean);

    const cardNoVal = cardData.cardNo || cardData.CardNo || "";
    const rawExpiry = cardData.expiryDate || cardData.ExpiryDate || "";
    const expiryVal = rawExpiry ? rawExpiry.split("T")[0] : "";

    return (
      <div className="p-1.5 sm:p-3.5 bg-slate-50/80 border border-slate-200/80 rounded-xl sm:rounded-2xl space-y-3 mt-3 animate-in fade-in slide-in-from-top-2 duration-300">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-bold text-slate-800 flex items-center gap-1.5">
            <DocumentCheckIcon className="w-4 h-4 text-primary-600" />
            {labelTitle} Details
          </p>
        </div>

        {cardKey === "whiteCard" && (
          <div className="p-2 sm:p-2.5 bg-blue-50/80 border border-blue-200/70 rounded-xl flex items-start gap-2 text-blue-900 text-[11px] leading-snug">
            <InformationCircleIcon className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">Note: </span>
              Entering your White Card details is optional, but providing your card number or a photo of your card helps site management verify your induction faster.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="block text-[10px] font-bold text-slate-600 mb-1">
              Card Number {isCardNoRequired ? <span className="text-rose-500">*</span> : <span className="text-slate-400 font-normal">(Optional)</span>}
            </label>
            <input
              type="text"
              placeholder="WC-987654"
              value={cardNoVal}
              disabled={isSubmitting}
              onChange={(e) => handleCardFieldChange(cardKey, "cardNo", e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/10 font-mono shadow-xs disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed"
              required={isCardNoRequired}
            />
            {isCardNoRequired && !cardNoVal?.trim() && (
              <p className="text-[9px] text-rose-500 font-semibold mt-1">
                Card number is required.
              </p>
            )}
          </div>

          <div>
            <label className="block text-[10px] font-bold text-slate-600 mb-1">
              Expiry Date
            </label>
            <DatePicker
              options={{ dateFormat: "Y-m-d", altFormat: "d/m/Y", altInput: true, allowInput: false }}
              value={expiryVal}
              readOnly={true}
              disabled={isSubmitting}
              placeholder="DD/MM/YYYY"
              onChange={(dates) => {
                if (dates && dates.length > 0) {
                  const dateObj = dates[0];
                  const y = dateObj.getFullYear();
                  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
                  const d = String(dateObj.getDate()).padStart(2, "0");
                  handleCardFieldChange(cardKey, "expiryDate", `${y}-${m}-${d}`);
                } else {
                  handleCardFieldChange(cardKey, "expiryDate", "");
                }
              }}
              suffix={
                expiryVal && !isSubmitting ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardFieldChange(cardKey, "expiryDate", "");
                    }}
                    className="p-1 hover:bg-slate-200/60 rounded-full text-slate-400 hover:text-slate-600 cursor-pointer transition-all"
                    title="Clear Date"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                ) : null
              }
              className="w-full text-xs font-semibold text-slate-800"
            />
          </div>
        </div>

        {/* AttachmentsActions Component with 10 files and 20MB limit */}
        <div>
          <label className="block text-[10px] font-bold text-slate-600 mb-1.5">
            Attachments
          </label>
          <AttachmentsActions
            files={fileList}
            disabled={isSubmitting}
            onChange={(newFiles) => {
              if (isSubmitting) return;
              setCertificationsForm((prev) => ({
                ...prev,
                [cardKey]: {
                  ...(prev[cardKey] || {}),
                  files: newFiles,
                  file: newFiles[0] || null,
                  documents: newFiles,
                },
              }));
            }}
            maxFiles={10}
            maxFileSize={20 * 1024 * 1024}
            showDeviceUpload={true}
            addLabel={`Upload ${labelTitle}`}
            chooseFileLabel="Choose File"
            captureLabel="Take Photo"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300 font-sans">
      <div className="space-y-3">
        {/* 1. White Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-2 sm:p-3.5 shadow-2xs transition-all">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs font-bold text-slate-900">White Card</p>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200/80">
                  <InformationCircleIcon className="w-3 h-3 text-amber-600 shrink-0" />
                  Important for Verification
                </span>
              </div>
            </div>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl shrink-0">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleToggleCard("hasWhiteCard", true)}
                className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
                  isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${
                  certificationsForm.hasWhiteCard
                    ? "bg-primary-600 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Yes
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleToggleCard("hasWhiteCard", false)}
                className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
                  isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${
                  !certificationsForm.hasWhiteCard
                    ? "bg-slate-700 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                No
              </button>
            </div>
          </div>

          {!certificationsForm.hasWhiteCard && (
            <div className="mt-2.5 p-2 sm:p-2.5 bg-amber-50/70 border border-amber-200/60 rounded-xl flex items-start gap-2 text-amber-900 text-[11px] leading-snug">
              <InformationCircleIcon className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span>
                <strong>Warning:</strong> It is important to upload white card for verification and safety compliance.
              </span>
            </div>
          )}

          {certificationsForm.hasWhiteCard && renderCardFields("whiteCard", "White Card", false)}
        </div>

        {/* 2. Silica Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-2 sm:p-3.5 shadow-2xs transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-900">Silica Card/Certification</p>
            </div>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleToggleCard("hasSilicaCard", true)}
                className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
                  isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${
                  certificationsForm.hasSilicaCard
                    ? "bg-primary-600 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Yes
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleToggleCard("hasSilicaCard", false)}
                className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
                  isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${
                  !certificationsForm.hasSilicaCard
                    ? "bg-slate-700 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                No
              </button>
            </div>
          </div>

          {certificationsForm.hasSilicaCard && renderCardFields("silica", "Silica Card/Certification", false)}
        </div>

        {/* 3. Asbestos Card */}
        <div className="rounded-2xl border border-slate-200 bg-white p-2 sm:p-3.5 shadow-2xs transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-900">Asbestos Card/Certification</p>
            </div>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleToggleCard("hasAsbestosCard", true)}
                className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
                  isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${
                  certificationsForm.hasAsbestosCard
                    ? "bg-primary-600 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                Yes
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => handleToggleCard("hasAsbestosCard", false)}
                className={`px-3 py-1 text-[11px] font-bold rounded-lg transition-all ${
                  isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                } ${
                  !certificationsForm.hasAsbestosCard
                    ? "bg-slate-700 text-white shadow-xs"
                    : "text-slate-600 hover:text-slate-900"
                }`}
              >
                No
              </button>
            </div>
          </div>

          {certificationsForm.hasAsbestosCard && renderCardFields("asbestos", "Asbestos Card/Certification", false)}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-2 w-full">
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-xs disabled:opacity-50"
        >
          <ArrowLeftIcon className="w-3.5 h-3.5" />
          <span>Back</span>
        </button>

        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-500 hover:to-primary-600 active:scale-[0.98] text-white font-bold text-xs shadow-md hover:shadow-lg hover:shadow-primary-600/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
        >
          {isSubmitting ? (
            <>
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              <span>Saving & Checking In...</span>
            </>
          ) : (
            <>
              <CheckCircleIcon className="w-4 h-4" />
              <span>Complete & Check In</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
